import { useMemo } from 'react';
import styled from '@emotion/styled';
import momentTz from 'moment-timezone';
import { DatePicker as AntDatePicker } from 'antd';

import {
  FORTA_BLACK,
  FORTA_WHITE,
  FORTA_ERROR,
  FORTA_ORANGE,
  FORTA_GRAY2,
  FORTA_BLUE,
} from '../../utils/colors';
import CalendarOutlinedIcon from '../svgs/CalendarOutlinedIcon';
import 'antd/dist/antd.min.css';
import { Themes, DEFAULT_INPUT_HEIGHT } from '../../constants';
import { getThemeColor } from '../../utils/common';

import { Box, Typography } from '@mui/material';

const { RangePicker } = AntDatePicker;
const StyledAntdDateRangePicker = styled((props: any) => <RangePicker {...props} />, {
  shouldForwardProp: (prop) =>
    prop !== 'colorTheme' &&
    prop !== 'coloredBackground' &&
    prop !== 'error' &&
    prop !== 'inputHeight',
})<any>(({ colorTheme, coloredBackground, inputHeight, error, sx }) => ({
  cursor: 'pointer',
  borderRadius: '10px',
  height: inputHeight,
  width: '100%',
  padding: '0 0.5em',
  backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
  border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
  '&:focus-within': {
    border: error ? `2px solid ${FORTA_ERROR}` : `1px solid ${FORTA_BLUE.light1}`,
  },
  boxShadow: `0px 4px 4px ${getThemeColor(colorTheme).light2}`,
  '&:before': error
    ? {
        content: '"!"',
        width: 21,
        height: 21,
        backgroundColor: FORTA_ORANGE,
        boxShadow: '0px 4px 4px rgba(0, 0, 0, 0.25)',
        color: FORTA_WHITE,
        fontWeight: 'bold',
        borderRadius: '50%',
        position: 'absolute',
        left: 5,
        top: 7,
        bottom: 0,
        textAlign: 'center',
      }
    : {},
  '& .ant-picker-suffix': {
    color: getThemeColor(colorTheme).primary,
  },
  '&.ant-picker-disabled': {
    backgroundColor: coloredBackground ? getThemeColor(colorTheme).light3 : FORTA_WHITE,
    '& .ant-picker-input>input[disabled]': {
      color: FORTA_BLACK,
    },
    '& .ant-picker-suffix': {
      color: FORTA_WHITE,
    },
  },
  // Style for range picker quick range buttons
  '& .ant-picker-ranges-item': {
    '&:hover': {
      color: FORTA_BLUE.primary,
    },
  },
  '& .ant-picker-ranges-item-active': {
    color: FORTA_WHITE,
    backgroundColor: FORTA_BLUE.primary,
    '&:hover': {
      color: FORTA_WHITE,
      backgroundColor: FORTA_BLUE.primary,
    },
  },
  ...sx,
}));

const FHAntdDateRangePicker = (props: any) => {
  const {
    colorTheme = Themes.Primary,
    coloredBackground = false,
    inputHeight = DEFAULT_INPUT_HEIGHT,
    error = false,
  } = props;

  const userTimezone = momentTz.tz.guess(true);
  const nowInUserTimezone = momentTz().tz(userTimezone);

  // Define quick ranges: end date is always current datetime, start date is calculated
  // Memoize ranges to ensure stable references for Ant Design's range matching
  const ranges = useMemo(() => {
    return {
      '1W': [momentTz().tz(userTimezone).subtract(1, 'week').startOf('day'), nowInUserTimezone],
      '1M': [momentTz().tz(userTimezone).subtract(30, 'days').startOf('day'), nowInUserTimezone],
      '3M': [momentTz().tz(userTimezone).subtract(3, 'months').startOf('day'), nowInUserTimezone],
      '6M': [momentTz().tz(userTimezone).subtract(6, 'months').startOf('day'), nowInUserTimezone],
      '1Y': [momentTz().tz(userTimezone).subtract(1, 'year').startOf('day'), nowInUserTimezone],
    };
  }, [userTimezone, nowInUserTimezone]);

  return (
    <StyledAntdDateRangePicker
      {...props}
      error={error}
      inputHeight={inputHeight}
      colorTheme={colorTheme}
      suffixIcon={<CalendarOutlinedIcon color={getThemeColor(colorTheme).primary} />}
      coloredBackground={coloredBackground}
      ranges={ranges}
      renderExtraFooter={() => (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography
            sx={{
              height: 40,
              cursor: 'pointer',
              p: 1,
              borderLeft: 'none',
              backgroundColor: FORTA_GRAY2,
            }}
          >
            {`${userTimezone} (${nowInUserTimezone.format('z')})`}
          </Typography>
        </Box>
      )}
    />
  );
};

export default FHAntdDateRangePicker;
